import React, { useState } from 'react';
import { ApolloProvider, useQuery, useApolloClient } from '@apollo/client';
import withTranslation from '@helpers/i18n';
import styled from 'styled-components';
import getApiClient, { SESSION_KEY } from 'react/services/api_client';
import PendingSatisfactionSurveysQuery from './pending_satisfaction_surveys.graphql';
import SatisfactionMeter from './index';

const apiClient = getApiClient();

const Banner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background-color: color-mix(in srgb, var(--primary) 85%, transparent);
  color: white;
  padding: 12px 20px;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 12px;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }
`;

const BannerTitle = styled.p`
  font-weight: 600;
  margin-bottom: 10px;
`;

const SurveyList = styled.ul`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  list-style: none;
`;

const TrainingLink = styled.button`
  display: flex;
  align-items: center;
  padding: 4px 10px;
  background: white;
  color: var(--primary);
  border-radius: 20px;
  border: none;
  cursor: pointer;
  font-weight: 700;
`;

const OverflowBadge = styled.span`
  display: flex;
  align-items: center;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.25);
  color: white;
  border-radius: 20px;
  font-weight: 700;
`;

const VISIBLE_LIMIT = 5;

const truncateName = (name, max = 25) => (name.length > max ? `${name.slice(0, max)}…` : name);

const SatisfactionSurveyBanner = ({ t }) => {
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [completedIds, setCompletedIds] = useState([]);
  const client = useApolloClient();

  const { data } = useQuery(PendingSatisfactionSurveysQuery, {
    fetchPolicy: 'cache-first',
  });

  const surveys = (data?.pending_satisfaction_surveys || []).filter(
    (s) => !completedIds.includes(s.id),
  );

  const handleSurveySubmitted = () => {
    const submittedId = selectedSurvey?.id;
    setCompletedIds((prev) => [...prev, submittedId]);
    client.cache.modify({
      fields: {
        pending_satisfaction_surveys(existingRefs = [], { readField }) {
          return existingRefs.filter((ref) => readField('id', ref) !== submittedId);
        },
      },
    });
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(client.cache.extract()));
    } catch (e) {
      /* ignore */
    }
    setSelectedSurvey(null);
  };

  if (surveys.length === 0 || dismissed) return null;

  const visibleSurveys = surveys.slice(0, VISIBLE_LIMIT);
  const hiddenCount = surveys.length - VISIBLE_LIMIT;

  return (
    <>
      <Banner data-purpose="satisfaction_survey_banner">
        <CloseButton
          data-purpose="satisfaction_survey_banner_close"
          onClick={() => setDismissed(true)}
        >
          <i className="material-icons">close</i>
        </CloseButton>
        <BannerTitle>{t('.your_feedback_helps')}</BannerTitle>
        <SurveyList>
          {visibleSurveys.map((survey) => (
            <li key={survey.id}>
              <TrainingLink
                data-purpose="satisfaction_survey_banner_training_link"
                onClick={() => setSelectedSurvey(survey)}
              >
                <i className="material-icons font-21 mie-5">assignment</i>
                <span title={survey.training_name}>{truncateName(survey.training_name)}</span>
              </TrainingLink>
            </li>
          ))}
          {hiddenCount > 0 && (
            <li>
              <OverflowBadge data-purpose="satisfaction_survey_banner_overflow_badge">
                +{hiddenCount}
              </OverflowBadge>
            </li>
          )}
        </SurveyList>
      </Banner>

      <SatisfactionMeter
        key={selectedSurvey?.id}
        participationId={selectedSurvey?.id}
        trainingName={selectedSurvey?.training_name}
        revealModal={!!selectedSurvey}
        onSubmitted={handleSurveySubmitted}
      />
    </>
  );
};

const SatisfactionSurveyBannerWithProvider = (props) => {
  return (
    <ApolloProvider client={apiClient}>
      <SatisfactionSurveyBanner {...props} />
    </ApolloProvider>
  );
};

export default withTranslation('js.components.satisfaction_meter')(
  SatisfactionSurveyBannerWithProvider,
);
